import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GETUsers, GetAdresses } from '../../services/users';
import { GETBrands } from '../../services/brands';
import { GETServices } from '../../services/service';
import { AdminCreateConsultancy } from '../../services/consultancy';
import { IService } from '../../utils/entities/interface';
import { capitalizeWords } from '../../utils/helper';
import useSnackbar from '../../hooks/useSnackbar';
import { ImageUpload } from '../../components/atoms/UploadImage';
import {
  darkDialogPaperProps,
  darkFieldSx,
  darkMenuPaperSx,
  darkTitleSx,
} from '../../components/common/darkFormStyles';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

// Matches the `consultancy/photos` case added to the backend's
// getFolderPath(type) switch — keeps this upload in its own S3 folder like
// every other presigned-URL upload type in the app.
const CONSULTANCY_PHOTO_FOLDER_TYPE = 14;
const MAX_PHOTOS = 6;

const AddConsultancyRequest = ({ open, onClose, onSaved }: Props) => {
  const [users, setUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);

  const [addresses, setAddresses] = useState<any[]>([]);
  const [addressId, setAddressId] = useState('');
  const [addressesLoading, setAddressesLoading] = useState(false);

  const [brands, setBrands] = useState<any[]>([]);
  const [brandId, setBrandId] = useState('');
  const [brandsLoading, setBrandsLoading] = useState(false);

  const [services, setServices] = useState<IService[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [serviceName, setServiceName] = useState<string[]>([]);

  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('FIRST_HALF');
  const [place, setPlace] = useState('residential');
  const [quantity, setQuantity] = useState('');
  const [alternatePhone, setAlternatePhone] = useState('');
  const [comment, setComment] = useState('');
  // Each slot is either an uploaded photo's S3 URL, or '' for an empty
  // (not-yet-used) upload tile. There's always exactly one trailing empty
  // slot so there's somewhere to click to add the next photo, up to
  // MAX_PHOTOS.
  const [photoSlots, setPhotoSlots] = useState<string[]>(['']);
  const [saving, setSaving] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const userId = selectedUser?._id || '';

  // Customer search (debounced)
  useEffect(() => {
    if (!open) return;
    const delay = setTimeout(() => {
      setUsersLoading(true);
      GETUsers({ page: 1, limit: 50, searchQuery: userSearch.trim() })
        .then((response) => setUsers(response.data || []))
        .finally(() => setUsersLoading(false));
    }, 400);

    return () => clearTimeout(delay);
  }, [userSearch, open]);

  // Addresses load once a customer is picked
  useEffect(() => {
    if (!userId) {
      setAddresses([]);
      setAddressId('');
      return;
    }
    setAddressesLoading(true);
    GetAdresses(userId)
      .then((response) => setAddresses(response.data || []))
      .finally(() => setAddressesLoading(false));
  }, [userId]);

  // Brand + Service catalogs load once the modal opens
  useEffect(() => {
    if (!open) return;
    setBrandsLoading(true);
    GETBrands({ page: 1, limit: 100 })
      .then((response) => setBrands(response.data || []))
      .finally(() => setBrandsLoading(false));

    setServicesLoading(true);
    GETServices({ page: 1, limit: 100 })
      .then((response) => setServices(response.data || []))
      .catch(() => setServices([]))
      .finally(() => setServicesLoading(false));
  }, [open]);

  const resetForm = () => {
    setSelectedUser(null);
    setUserSearch('');
    setAddressId('');
    setBrandId('');
    setServiceName([]);
    setDate('');
    setSlot('FIRST_HALF');
    setPlace('residential');
    setQuantity('');
    setAlternatePhone('');
    setComment('');
    setPhotoSlots(['']);
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handlePhotoChange = (index: number, url: string) => {
    setPhotoSlots((prev) => {
      const next = [...prev];
      next[index] = url;
      // Once every visible slot is filled, add a fresh empty one so there's
      // still a tile to click for the next photo (capped at MAX_PHOTOS).
      if (next.length < MAX_PHOTOS && next.every(Boolean)) {
        next.push('');
      }
      return next;
    });
  };

  const handlePhotoRemove = (index: number) => {
    setPhotoSlots((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [''];
    });
  };

  const handleSubmit = async () => {
    if (!userId || !addressId || !brandId || !date || !slot) {
      showSnackbar('Customer, address, brand, date and slot are required', 'error');
      return;
    }

    try {
      setSaving(true);
      await AdminCreateConsultancy({
        user_id: userId,
        addressId,
        brandId,
        serviceName,
        date,
        slot,
        place,
        quantity,
        alternatePhone,
        comment,
        photos: photoSlots.filter(Boolean),
      });
      showSnackbar('Free consultancy request created', 'success');
      onSaved?.();
      resetForm();
      onClose();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to create request', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={darkDialogPaperProps}
    >
      {SnackbarComponent}

      <DialogTitle sx={darkTitleSx}>
        Add Free Consultancy Request
        <IconButton onClick={handleClose} sx={{ color: '#9e9e9e' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: '#2a2a2a' }} />

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Autocomplete
            options={users}
            value={selectedUser}
            loading={usersLoading}
            onChange={(_event, newValue) => setSelectedUser(newValue)}
            onInputChange={(_event, newInputValue) => setUserSearch(newInputValue)}
            getOptionLabel={(option) =>
              `${capitalizeWords(option.name || '') || option.phoneNumber} - ${option.phoneNumber}`
            }
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={darkFieldSx}
            slotProps={{ paper: darkMenuPaperSx }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer"
                placeholder="Search by name or phone"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress size={16} sx={{ color: '#E31E25' }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControl fullWidth disabled={!userId || addressesLoading} sx={darkFieldSx}>
            <InputLabel>Address</InputLabel>
            <Select
              value={addressId}
              label="Address"
              onChange={(event) => setAddressId(event.target.value)}
              MenuProps={{ PaperProps: darkMenuPaperSx }}
            >
              {addresses.map((address) => (
                <MenuItem key={address._id} value={address._id}>
                  {[address.house, address.street, address.city, address.zipcode].filter(Boolean).join(', ')}
                </MenuItem>
              ))}
              {!addressesLoading && userId && addresses.length === 0 && (
                <MenuItem disabled value="">
                  No saved addresses for this customer
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={brandsLoading} sx={darkFieldSx}>
            <InputLabel>Brand</InputLabel>
            <Select
              value={brandId}
              label="Brand"
              onChange={(event) => setBrandId(event.target.value)}
              MenuProps={{ PaperProps: darkMenuPaperSx }}
            >
              {brands.map((brand) => (
                <MenuItem key={brand._id} value={brand._id}>
                  {brand.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            freeSolo
            loading={servicesLoading}
            options={services.map((service) => service.name)}
            value={serviceName}
            onChange={(_event, newValue) => setServiceName(newValue as string[])}
            sx={darkFieldSx}
            slotProps={{ paper: darkMenuPaperSx }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Services"
                placeholder={servicesLoading ? 'Loading services…' : 'Pick from the catalog or type a custom one'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {servicesLoading ? <CircularProgress size={16} sx={{ color: '#E31E25' }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              type="date"
              label="Preferred Date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...darkFieldSx,
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                  cursor: 'pointer',
                },
              }}
            />
            <FormControl fullWidth sx={darkFieldSx}>
              <InputLabel>Slot</InputLabel>
              <Select
                value={slot}
                label="Slot"
                onChange={(event) => setSlot(event.target.value)}
                MenuProps={{ PaperProps: darkMenuPaperSx }}
              >
                <MenuItem value="FIRST_HALF">1st Half</MenuItem>
                <MenuItem value="SECOND_HALF">2nd Half</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <FormControl fullWidth sx={darkFieldSx}>
            <InputLabel>Place Type</InputLabel>
            <Select
              value={place}
              label="Place Type"
              onChange={(event) => setPlace(event.target.value)}
              MenuProps={{ PaperProps: darkMenuPaperSx }}
            >
              <MenuItem value="residential">Residential</MenuItem>
              <MenuItem value="commercial">Commercial</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              onWheel={(event) => (event.target as HTMLElement).blur()}
              sx={darkFieldSx}
            />
            <TextField
              label="Alternate Phone"
              value={alternatePhone}
              onChange={(event) => setAlternatePhone(event.target.value)}
              sx={darkFieldSx}
            />
          </Box>

          <TextField
            label="Comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            multiline
            minRows={2}
            sx={darkFieldSx}
          />

          {/* The Consultancy model already stores `photos` (populated when
              the customer submits through the app) — this was the one field
              missing from the admin-created path, so admin-created requests
              could never carry photos even though the schema supports it.
              Each tile uploads straight to S3 via a presigned URL, same as
              every other image upload in the admin app. */}
          <Box>
            <Box sx={{ color: '#9e9e9e', fontSize: 13, fontWeight: 600, mb: 1 }}>
              Photos
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {photoSlots.map((url, index) => (
                <Box key={index} sx={{ width: 130 }}>
                  <ImageUpload
                    imageUrl={url}
                    onChange={(newUrl) => handlePhotoChange(index, newUrl)}
                    onRemove={() => handlePhotoRemove(index)}
                    uploadLabel="Add Photo"
                    fileId={`consultancy-photo-${index}`}
                    type="icon"
                    disabled={saving}
                    folderType={CONSULTANCY_PHOTO_FOLDER_TYPE}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: '#2a2a2a' }} />

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ color: '#9e9e9e' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
          sx={{
            background: '#E31E25',
            color: '#fff',
            px: 4,
            py: 1.1,
            borderRadius: '10px',
            fontWeight: 600,
            '&:hover': { background: '#C62828' },
            '&.Mui-disabled': { background: '#7a2a2a', color: '#ddd' },
          }}
        >
          {saving ? 'Saving…' : 'Create Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddConsultancyRequest;
